import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewContainerRef} from '@angular/core';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {BehaviorSubject, combineLatest, EMPTY, filter, finalize, map, Observable, of, switchMap, take, tap} from 'rxjs';
import {PermissionService} from 'src/app/permission/permission.service';
import {DialogService} from 'src/app/services/dialog.service';
import {SnackbarService} from 'src/app/services/snackbar.service';

import {AppSettingsService, SubdomainEnum} from 'src/app/services/app-settings.service';
import UnitCardStatus from 'src/app/shared/enums/unit-card/UnitCardStatus.enum';
import {UnitCardService} from 'src/app/services/shared/unit-card.service';
import {DynamicComponentItem} from 'src/app/shared/models/dynamic-component-loader/DynamicComponentItem.model';
import {BasePhoneVerificationStrategy} from 'src/app/shared/models/phone-verification-strategies/base-phone-verification-strategy.model';
import {UnitBusinessVirtualDebitCardDto} from 'src/app/shared/models/unit-card/UnitBusinessVirtualDebitCardDto.model';
import {AlertDialogParams} from '../../../alert-dialog/alert-dialog.component';
import {ConfirmDialogParams} from '../../../confirm-dialog/confirm-dialog.component';
import {NoAccessPermissionComponent} from '../../../dynamic-component-loader/no-access-permission.component';
import {PhoneVerificationDialogComponent} from '../../../phone-verification-dialog/phone-verification-dialog.component';
import {PhoneVerificationDialogContext} from '../../../phone-verification-dialog/PhoneVerificationDialogContext';
import {CreateCardDialogComponent} from '../create-card-dialog/create-card-dialog.component';
import {CreateCardDialogParams, CreateUnitCardStep} from '../create-card-dialog/CreateCardDialogParams';
import {EventsTelemetry, EventTelemetryInstance} from '../TelemetryEvent';
import {FinancialPermissionLevel} from 'src/app/permission/enums/financialPermissionLevel.enum';
import PermissionLevel from 'src/app/permission/enums/permissionLevel.enum';
import {NoPermissionAction} from 'src/app/shared/directives/check-permissions.directive';
import UnitDepositAccountStatus from '../../../../enums/UnitDepositAccountStatus.enum';
import {UnitCardPinAction} from '../unit-card-pin-dialog/UnitCardPinContext';

@Component({
  selector: 'terra-unit-card-empty',
  templateUrl: './unit-card-empty.component.html',
  styleUrls: ['./unit-card-empty.component.scss'],
  providers: [
    {provide: BasePhoneVerificationStrategy, useExisting: UnitCardService},
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitCardEmptyComponent extends OnDestroyMixin implements OnInit {
  @Input() hasCard = false;

  disableActions = false;
  PermissionLevel = PermissionLevel;
  NoPermissionAction = NoPermissionAction;
  FinancialPermissionLevel = FinancialPermissionLevel;

  unitBusinessCards$ = this.unitCardService.unitBusinessCards$;

  userHasPermission$ = this.permissionService.haveAuthorizedSignatory$;

  maxNumberOfVirtualCards$ = combineLatest([
    this.unitBusinessCards$,
    this.unitCardService.unitBankAccount$,
  ]).pipe(
    tap(([unitBusinessCards, unitBankAccount]) => this.maxCardsAllowed = unitBankAccount.maxNumberOfVirtualCards),
    map(
      ([unitBusinessCards, unitBankAccount]) =>
        (unitBusinessCards?.filter(
          (c) =>
            c.cardStatus !== UnitCardStatus.Draft &&
            c.cardStatus !== UnitCardStatus.ClosedByCustomer
        )?.length || 0) >= unitBankAccount.maxNumberOfVirtualCards
    )
  );

  isLoading$ = new BehaviorSubject<boolean>(false);

  isLpSide = this.appSettingService.subDomain === SubdomainEnum.LP;
  unitBankAccountId: number;
  unitBankAccountBalance: number;
  unitBankDepositProduct: string;

  private maxCardsAllowed = 1;
  private newCardMinBalanceCents = 5000;

  constructor(
    private unitCardService: UnitCardService,
    private dialog: MatDialog,
    private vcr: ViewContainerRef,
    private permissionService: PermissionService,
    private dialogService: DialogService,
    private snackbarService: SnackbarService,
    private appSettingService: AppSettingsService,
    private cdr: ChangeDetectorRef
  ) {
    super();

    this.unitCardService.unitBankAccount$
      .pipe(untilComponentDestroyed(this))
      .subscribe((unitBankAccount) => {
        this.unitBankAccountId = unitBankAccount.id;
        this.unitBankAccountBalance = unitBankAccount.balance;
        this.unitBankDepositProduct = unitBankAccount.depositProduct;
        this.disableActions =
          unitBankAccount.status !== UnitDepositAccountStatus.Open;
      });
  }

  ngOnInit(): void {
  }

  private nonSignatoryPermission() {
    const includeDynamicComponent = new DynamicComponentItem(
      NoAccessPermissionComponent,
      {
        title: 'You don’t have permissions to issue a debit card',
        subTitle:
          'Please reach out a user with financial-signatory permissions for assistance',
      }
    );

    const confirmDialogParams = new ConfirmDialogParams();
    confirmDialogParams.title = 'Add Virtual Debit Card';
    confirmDialogParams.dynamicComponent = includeDynamicComponent;
    confirmDialogParams.actionLabel = 'close';
    confirmDialogParams.panelClass = 'add-card-non-signatory-permission';
    confirmDialogParams.hideCancel = true;
    return this.dialogService
      .confirmDialog(confirmDialogParams)
      .afterClosed()
      .pipe(switchMap((_) => EMPTY));
  }

  private notEnoughBalanceForNewCard() {
    const alertDialogParams = new AlertDialogParams();
    alertDialogParams.title = 'Add Funds to Your Account';
    alertDialogParams.description = 'Please fund your account with more than $50 to qualify for a free debit card.';

    return this.dialogService
      .alertDialog(alertDialogParams)
      .afterClosed()
      .pipe(switchMap((_) => EMPTY));
  }

  private createCardFlow() {
    let newBusinessCardCreated = null;
    const config = new MatDialogConfig<CreateCardDialogParams>();
    config.disableClose = true;
    config.closeOnNavigation = true;
    config.panelClass = 'create-unit-card-dialog';
    let newCardId = null;

    this.writeEventToTelemetry(
      this.isLpSide
        ? EventsTelemetry.LP_DebitCardCreateStart
        : EventsTelemetry.GP_DebitCardCreateStart
    );

    return this.maxNumberOfVirtualCards$.pipe(
      take(1),
      filter((maxNumberOfVirtualCards) => {
        if (!!maxNumberOfVirtualCards) {
          this.maxNumberOfVirtualCardsDialog();
          this.writeEventToTelemetry(
            this.isLpSide
              ? EventsTelemetry.LP_DebitCardCreateMaxCards
              : EventsTelemetry.GP_DebitCardCreateMaxCards
          );
          this.isLoading$.next(false);
        }
        return !maxNumberOfVirtualCards;
      }),
      switchMap((_) =>
        this.unitCardService.unitBusinessCardDraft$.pipe(take(1))
      ),
      switchMap((unitBusinessCardDraft) => {
        config.data = new CreateCardDialogParams(
          CreateUnitCardStep.FillDetails,
          unitBusinessCardDraft
        );
        return this.dialog
          .open(CreateCardDialogComponent, config)
          .afterClosed();
      }),
      filter((obj) => {
        if (!obj) {
          this.isLoading$.next(false);
        }
        return !!obj;
      }),
      map((model) => model as UnitBusinessVirtualDebitCardDto),
      switchMap((model) =>
        model.id > 0
          ? this.unitCardService.updateBusinessDebitCard(model.id, model)
          : this.unitCardService.saveBusinessDebitCardDraft(model)
      ),
      tap((model) => (newCardId = model.id)),
      switchMap((model) => {
        this.cdr.markForCheck();
        return this.verifyPhoneProcess(model);
      }),
      filter((isVerified) => {
        if (!isVerified) {
          this.isLoading$.next(false);
        }
        this.writeEventToTelemetry(
          this.isLpSide
            ? EventsTelemetry.LP_DebitCardCreateOTPCodeVerified
            : EventsTelemetry.GP_DebitCardCreateOTPCodeVerified
        );
        return isVerified;
      }),
      switchMap((_) =>
        this.unitCardService.createBusinessDebitCardOnUnit(newCardId)
      ),
      switchMap((newCardCreated) => {
        this.cdr.markForCheck();
        newBusinessCardCreated = {...newCardCreated, isPinCodeExist: false};
        config.data = new CreateCardDialogParams(
          CreateUnitCardStep.Created,
          newBusinessCardCreated
        );
        return this.dialog
          .open(CreateCardDialogComponent, config)
          .afterClosed();
      }),
      tap((_) =>
        !!newBusinessCardCreated
          ? this.unitCardService.addUnitBusinessCards(newBusinessCardCreated)
          : ''
      ),
      switchMap((result) =>
        result
          ? this.unitCardService.doDebitCardPinCodeAction(
            UnitCardPinAction.SET_PIN,
            'PIN code saved successfully',
            'PIN code saving failed!'
          )
          : of(null)
      ),
      finalize(() => this.isLoading$.next(false))
    );
  }

  createNewDebitCard() {
    this.isLoading$.next(true);

    this.writeEventToTelemetry(
      this.isLpSide
        ? EventsTelemetry.LP_DebitCardIssueClick
        : EventsTelemetry.GP_DebitCardIssueClick,
      this.unitBankDepositProduct
    );

    this.userHasPermission$
      .pipe(
        take(1),
        switchMap((userHasPermission) => {
            if (['tier1L', 'tier2L', 'tier3L'].includes(this.unitBankDepositProduct) && this.unitBankAccountBalance < this.newCardMinBalanceCents) {
              this.writeEventToTelemetry(
                this.isLpSide
                  ? EventsTelemetry.LP_DebitCardIssueInsufficientFunds
                  : EventsTelemetry.GP_DebitCardIssueInsufficientFunds,
                this.unitBankDepositProduct
              );

              return this.notEnoughBalanceForNewCard();
            } else {
              this.writeEventToTelemetry(
                this.isLpSide
                  ? EventsTelemetry.LP_DebitCardIssueStartedOk
                  : EventsTelemetry.GP_DebitCardIssueStartedOk,
                this.unitBankDepositProduct
              );

              return userHasPermission ? this.createCardFlow() : this.nonSignatoryPermission();
            }
          }
        ),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe({
        next: (next) => {
          this.writeEventToTelemetry(
            this.isLpSide
              ? EventsTelemetry.LP_DebitCardCreateSuccess
              : EventsTelemetry.GP_DebitCardCreateSuccess
          );
          this.snackbarService.showGeneralMessage(
            'Business virtual debit card created successfully'
          );
        },
        error: (error) => {
          this.writeEventToTelemetry(
            this.isLpSide
              ? EventsTelemetry.LP_DebitCardCreateUNITFailed
              : EventsTelemetry.GP_DebitCardCreateUNITFailed
          );
          this.snackbarService.showGeneralMessage(
            'Business virtual debit card created failed'
          );
        },
      });
  }

  private maxNumberOfVirtualCardsDialog() {
    const confirmDialogParams = new AlertDialogParams();
    confirmDialogParams.title = 'Debit card issuing limit reached';
    confirmDialogParams.panelClass = 'unit-card-max-cards-dialog';
    confirmDialogParams.description = `<div>You have reached the card-issuing number limit.</div>
    <div>It’s allowed to add up to ${this.maxCardsAllowed} cards to the account.</div>
    <div>Contact Covercy support for assistance.</div>`;
    confirmDialogParams.actionLabel = 'Close';
    this.dialogService.alertDialog(confirmDialogParams);
  }

  private verifyPhoneProcess(
    businessCard: UnitBusinessVirtualDebitCardDto
  ): Observable<boolean> {
    const phoneNumberObject = businessCard.phone;
    const dialogConfig = new MatDialogConfig<PhoneVerificationDialogContext>();
    dialogConfig.data = new PhoneVerificationDialogContext();
    dialogConfig.data.phone = `${phoneNumberObject.countryCode}${phoneNumberObject.number}`;
    dialogConfig.data.isUserAbleToEditPhoneNumber = true;
    dialogConfig.data.sendSmsOnOpen = true;
    dialogConfig.viewContainerRef = this.vcr;
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = 'new-card-phone-verification-dialog';
    return this.dialog
      .open(PhoneVerificationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(take(1));
  }

  private writeEventToTelemetry(eventTelemetryInstance: EventTelemetryInstance, depositType: string = null) {
    this.unitCardService.writeEventToTelemetry(eventTelemetryInstance, depositType);
  }
}
